import { useEffect } from 'react';

/**
 * Custom hook to add keyboard navigation shortcuts for audit navigation
 * @param currentIndex Current position in the audit list
 * @param listLength Total length of the audit list
 * @param navigateToPage Function to call when navigating to a specific page
 */
export function useAuditNavigationShortcuts(
  currentIndex: number,
  listLength: number,
  navigateToPage: (index: number) => void,
): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd/Ctrl + Shift + Plus/Minus keys
      if (
        (event.key === '+' || event.key === '-' || event.key === '=') &&
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey
      ) {
        // Stop event propagation to prevent conflicts with other components
        event.stopPropagation();
        event.preventDefault();

        // Navigate to previous task with Cmd/Ctrl + Shift + Minus
        if (event.key === '-' && currentIndex > 0) {
          navigateToPage(currentIndex - 1);
        }

        // Navigate to next task with Cmd/Ctrl + Shift + Plus (or =)
        if ((event.key === '+' || event.key === '=') && currentIndex < listLength - 1) {
          navigateToPage(currentIndex + 1);
        }
      }
    };

    // Use capture phase to ensure this handler runs before lower-level components
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [currentIndex, listLength, navigateToPage]);
}
